import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Search, Plus, X, Upload, Download, Pencil, CheckCircle2, AlertTriangle, Copy } from "lucide-react";

const ROLES = ["principal", "assistant_principal", "instructional_coach", "teacher", "district_leader"];
const roleLabel = (r) => r.replace(/_/g, " ");
const ROW_BADGES = { ok: "badge-approved", extend: "badge-submitted", duplicate: "badge-neutral", error: "badge-nosend" };
const ROW_LABELS = { ok: "New contact", extend: "Adds school to existing", duplicate: "Duplicate — skipped", error: "Needs correction" };

const emptyAssoc = () => ({ school_id: "", role: "teacher", grade_level: "", team: "" });
const emptyForm = () => ({ first_name: "", last_name: "", email: "", status: "active", associations: [emptyAssoc()] });

function ContactForm({ initial, schools, onSaved, onCancel, testidPrefix }) {
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const setA = (i, patch) => setForm({ ...form, associations: form.associations.map((a, j) => (j === i ? { ...a, ...patch } : a)) });

  const save = async () => {
    setBusy(true);
    try {
      if (initial.id) await api.patch(`/contacts/${initial.id}`, form);
      else await api.post("/contacts", form);
      toast.success("Contact saved. Contact records never create login accounts or send email.");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card p-6 space-y-4 fade-up" data-testid={`${testidPrefix}-form`}>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div><label className="label">First name *</label>
          <input className="input" value={form.first_name} data-testid={`${testidPrefix}-first-name`}
            onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
        <div><label className="label">Last name *</label>
          <input className="input" value={form.last_name} data-testid={`${testidPrefix}-last-name`}
            onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
        <div><label className="label">Email *</label>
          <input className="input" type="email" value={form.email} data-testid={`${testidPrefix}-email`}
            onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div><label className="label">Status</label>
          <select className="input" value={form.status} data-testid={`${testidPrefix}-status`}
            onChange={(e) => setForm({ ...form, status: e.target.value })}>
            <option value="active">active</option><option value="inactive">inactive</option></select></div>
      </div>
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="label !mb-0">School associations (role & grade/team may differ per school)</label>
          <button type="button" className="btn-ghost !py-1 !px-2 text-xs" data-testid={`${testidPrefix}-add-association`}
            onClick={() => setForm({ ...form, associations: [...form.associations, emptyAssoc()] })}><Plus size={13} /> Add school</button>
        </div>
        <div className="space-y-2">
          {form.associations.map((a, i) => (
            <div key={i} className="grid grid-cols-2 lg:grid-cols-[1fr_180px_120px_140px_36px] gap-2 items-center" data-testid={`${testidPrefix}-assoc-${i}`}>
              <select className="input" value={a.school_id} data-testid={`${testidPrefix}-assoc-school-${i}`}
                onChange={(e) => setA(i, { school_id: e.target.value })}>
                <option value="">School…</option>
                {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select>
              <select className="input" value={a.role} data-testid={`${testidPrefix}-assoc-role-${i}`}
                onChange={(e) => setA(i, { role: e.target.value })}>
                {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}</select>
              <input className="input" placeholder="Grade" value={a.grade_level} data-testid={`${testidPrefix}-assoc-grade-${i}`}
                onChange={(e) => setA(i, { grade_level: e.target.value })} />
              <input className="input" placeholder="Team" value={a.team} data-testid={`${testidPrefix}-assoc-team-${i}`}
                onChange={(e) => setA(i, { team: e.target.value })} />
              <button type="button" className="btn-ghost !px-2 text-[#991B1B]" disabled={form.associations.length === 1}
                data-testid={`${testidPrefix}-assoc-remove-${i}`}
                onClick={() => setForm({ ...form, associations: form.associations.filter((_, j) => j !== i) })}><X size={14} /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button className="btn-primary" onClick={save} data-testid={`${testidPrefix}-save`}
          disabled={busy || !form.first_name || !form.last_name || !form.email || form.associations.some((a) => !a.school_id)}>
          {busy ? "Saving…" : "Save contact"}</button>
        <button className="btn-ghost" onClick={onCancel} data-testid={`${testidPrefix}-cancel`}>Cancel</button>
      </div>
    </div>
  );
}

function CsvImport({ onDone }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/contacts/csv-preview", fd);
      setPreview(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const editRow = (i, field, value) => {
    const rows = preview.rows.map((r, j) => (j === i ? { ...r, data: { ...r.data, [field]: value } } : r));
    setPreview({ ...preview, rows });
  };

  const recheck = async () => {
    const headers = ["first_name", "last_name", "email", "role", "district", "school", "grade_level", "team", "status"];
    const text = [headers.join(","), ...preview.rows.map((r) => headers.map((h) => `"${(r.data[h] || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const fd = new FormData();
    fd.append("file", new Blob([text], { type: "text/csv" }), "corrected.csv");
    setBusy(true);
    try { const { data } = await api.post("/contacts/csv-preview", fd); setPreview(data); toast.success("Re-validated."); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/csv-commit", { rows: preview.rows });
      toast.success(`Imported: ${data.created} created, ${data.extended} extended, ${data.skipped} skipped.`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    const r = await api.get("/contacts/template", { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = "contacts-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card p-6 space-y-4 fade-up" data-testid="csv-import-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-serif font-bold text-lg text-navy">CSV bulk upload</h3>
          <p className="text-xs text-[#78716C]">One row per person per school — the same email stays one contact with multiple school associations. Importing creates contact records only: no login accounts, no emails sent.</p>
        </div>
        <button className="btn-outline !py-1.5" onClick={downloadTemplate} data-testid="csv-template-download"><Download size={14} /> Download template (with fictional examples)</button>
      </div>
      <label className="flex items-center justify-center gap-3 rounded-xl border-2 border-dashed border-[#E7E1D4] bg-canvas px-6 py-8 cursor-pointer hover:border-terracotta transition-colors" data-testid="csv-upload-dropzone">
        <Upload size={18} className="text-terracotta" />
        <span className="text-sm font-bold text-navy">{busy ? "Validating…" : "Upload contacts CSV for preview"}</span>
        <input type="file" accept=".csv" className="hidden" disabled={busy} onChange={(e) => upload(e.target.files?.[0])} data-testid="csv-file-input" />
      </label>
      {preview && (
        <div className="space-y-3" data-testid="csv-preview">
          <div className="flex flex-wrap gap-2">
            {Object.entries(preview.counts).map(([k, v]) => (
              <span key={k} className={ROW_BADGES[k]} data-testid={`csv-count-${k}`}>{ROW_LABELS[k]}: {v}</span>
            ))}
          </div>
          <p className="text-xs text-[#78716C]">{preview.note}</p>
          <div className="overflow-x-auto rounded-lg border border-[#E7E1D4]">
            <table className="w-full min-w-[900px]">
              <thead className="bg-sand/60"><tr>
                <th className="table-th">Row</th><th className="table-th">First</th><th className="table-th">Last</th>
                <th className="table-th">Email</th><th className="table-th">Role</th><th className="table-th">School</th>
                <th className="table-th">Status</th><th className="table-th">Messages</th></tr></thead>
              <tbody>
                {preview.rows.map((r, i) => (
                  <tr key={i} className={r.status === "error" ? "bg-[#FEF2F2]" : ""} data-testid={`csv-row-${i}`}>
                    <td className="table-td text-xs">{r.row}</td>
                    {["first_name", "last_name", "email", "role", "school"].map((f) => (
                      <td key={f} className="table-td !py-1.5">
                        <input className="input !py-1 !px-2 text-xs min-w-[110px]" value={r.data[f]}
                          data-testid={`csv-edit-${i}-${f}`} onChange={(e) => editRow(i, f, e.target.value)} /></td>
                    ))}
                    <td className="table-td"><span className={ROW_BADGES[r.status]}>{ROW_LABELS[r.status]}</span></td>
                    <td className="table-td text-xs text-[#78716C] max-w-[260px]">{r.messages.join("; ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex gap-2">
            <button className="btn-outline" onClick={recheck} disabled={busy} data-testid="csv-recheck-button">Re-validate corrections</button>
            <button className="btn-primary" onClick={commit} disabled={busy || preview.counts.error > 0} data-testid="csv-commit-button">
              <CheckCircle2 size={15} /> {preview.counts.error > 0 ? "Fix errors before saving" : `Save ${preview.counts.ok + preview.counts.extend} row(s)`}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Contacts() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [contacts, setContacts] = useState(null);
  const [search, setSearch] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [schools, setSchools] = useState([]);
  const [panel, setPanel] = useState(""); // "add" | "csv" | "edit"
  const [editing, setEditing] = useState(null);

  const load = () => {
    const q = new URLSearchParams();
    if (search) q.set("search", search);
    if (role) q.set("role", role);
    if (status) q.set("status", status);
    api.get(`/contacts?${q}`).then((r) => setContacts(r.data));
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [search, role, status]);
  useEffect(() => { api.get("/organizations").then((r) => setSchools(r.data.flatMap((o) => o.schools))); }, []);

  return (
    <div className="max-w-6xl space-y-6" data-testid="contacts-page">
      <div className="flex flex-wrap items-end justify-between gap-4 fade-up">
        <div>
          <p className="eyebrow">Directory</p>
          <h1 className="section-title text-3xl mt-1">Contacts</h1>
          <p className="mt-3 text-sm text-[#57534E]">Principals, assistant principals, coaches, and teachers. Contact records only — they cannot log in.</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button className="btn-outline" onClick={() => { setPanel(panel === "csv" ? "" : "csv"); setEditing(null); }} data-testid="csv-import-button">
              <Upload size={15} /> CSV import</button>
            <button className="btn-primary" onClick={() => { setPanel(panel === "add" ? "" : "add"); setEditing(null); }} data-testid="add-contact-button">
              {panel === "add" ? <X size={15} /> : <Plus size={15} />} Add contact</button>
          </div>
        )}
      </div>

      {panel === "add" && isAdmin && (
        <ContactForm initial={emptyForm()} schools={schools} testidPrefix="add-contact"
          onSaved={() => { setPanel(""); load(); }} onCancel={() => setPanel("")} />)}
      {panel === "csv" && isAdmin && <CsvImport onDone={() => { setPanel(""); load(); }} />}
      {editing && (
        <ContactForm initial={{ ...editing, associations: editing.associations.length ? editing.associations : [emptyAssoc()] }}
          schools={schools} testidPrefix="edit-contact" onSaved={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />)}

      <div className="flex flex-wrap gap-3 fade-up-1" data-testid="contact-filters">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#78716C]" />
          <input className="input !pl-9" placeholder="Search name or email…" value={search}
            onChange={(e) => setSearch(e.target.value)} data-testid="contact-search-input" />
        </div>
        <select className="input max-w-[220px]" value={role} onChange={(e) => setRole(e.target.value)} data-testid="contact-role-filter">
          <option value="">All roles</option>
          {ROLES.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
        </select>
        <select className="input max-w-[160px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="contact-status-filter">
          <option value="">Any status</option><option value="active">active</option><option value="inactive">inactive</option>
        </select>
      </div>

      <div className="card overflow-hidden fade-up-2" data-testid="contacts-table-card">
        <table className="w-full">
          <thead className="bg-sand/60">
            <tr><th className="table-th">Name</th><th className="table-th">Email</th>
              <th className="table-th">Schools & roles</th><th className="table-th">Status</th>{isAdmin && <th className="table-th"></th>}</tr>
          </thead>
          <tbody>
            {(contacts || []).map((c) => (
              <tr key={c.id} className="hover:bg-canvas transition-colors" data-testid={`contact-row-${c.id}`}>
                <td className="table-td font-bold">{c.display_name} {c.is_sample && <span className="badge-neutral ml-1">Sample</span>}</td>
                <td className="table-td text-[#57534E]">{c.email}</td>
                <td className="table-td">
                  <div className="flex flex-wrap gap-1.5">
                    {c.associations.map((a, i) => (
                      <span key={i} className="badge-neutral" data-testid={`contact-assoc-${c.id}-${i}`}>
                        {a.school_name || "?"} — {roleLabel(a.role)}{a.grade_level ? ` · Gr ${a.grade_level}` : ""}{a.team ? ` · ${a.team}` : ""}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="table-td"><span className={c.status === "active" ? "badge-approved" : "badge-neutral"}>{c.status}</span></td>
                {isAdmin && (
                  <td className="table-td">
                    <button className="btn-ghost !p-1.5" onClick={() => { setEditing(c); setPanel(""); }} data-testid={`edit-contact-${c.id}`}><Pencil size={14} /></button>
                  </td>)}
              </tr>
            ))}
            {contacts && contacts.length === 0 && (
              <tr><td colSpan={5} className="table-td text-center text-[#78716C] py-10" data-testid="contacts-empty">No contacts match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
